import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { usePraxis } from "../context/PraxisContext";
import { getConsent } from "../utils/consent";

const STORAGE_KEY = "praxis_news_seen_v1";
const SESSION_KEY = "praxis_news_dismissed_session";

export default function NewsModal() {
  const { popup, defaults } = usePraxis();
  const [open, setOpen] = useState(false);
  const [dontShow, setDontShow] = useState(false);

  // Effektive Werte: DB-Text oder Default-Vorlage
  const eff = {
    enabled: popup?.enabled ?? defaults.popup.enabled,
    delay: Number(popup?.delay_seconds ?? defaults.popup.delay_seconds) || 60,
    badge: (popup?.badge && popup.badge.trim()) || defaults.popup.badge,
    title: (popup?.title && popup.title.trim()) || defaults.popup.title,
    text: (popup?.text && popup.text.trim()) || defaults.popup.text,
    bullets: (popup?.bullets && popup.bullets.trim()) || defaults.popup.bullets,
    note: (popup?.note && popup.note.trim()) || defaults.popup.note,
    ctaLabel: (popup?.cta_label && popup.cta_label.trim()) || defaults.popup.cta_label,
    ctaLink: (popup?.cta_link && popup.cta_link.trim()) || defaults.popup.cta_link,
  };
  const bulletsArr = eff.bullets.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
  const isEnabled = String(eff.enabled) === "1" || eff.enabled === true;

  useEffect(() => {
    if (!isEnabled) return;
    const shouldSkip = () =>
      localStorage.getItem(STORAGE_KEY) ||
      sessionStorage.getItem(SESSION_KEY);

    let timer = null;
    const schedule = () => {
      if (timer || shouldSkip()) return;
      timer = setTimeout(() => {
        if (!shouldSkip()) setOpen(true);
      }, eff.delay * 1000);
    };

    if (getConsent()) schedule();

    const handler = () => {
      if (getConsent()) schedule();
    };
    window.addEventListener("consent-changed", handler);
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener("consent-changed", handler);
    };
  }, [isEnabled, eff.delay]);

  const close = () => {
    if (dontShow) localStorage.setItem(STORAGE_KEY, "1");
    sessionStorage.setItem(SESSION_KEY, "1");
    setOpen(false);
  };

  if (!open || !isEnabled) return null;

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" data-testid="news-modal">
      <div className="modal-card">
        <button className="modal-close" onClick={close} aria-label="Schließen" data-testid="news-modal-close">
          <X size={20} />
        </button>
        {eff.badge && <span className="badge">{eff.badge}</span>}
        {eff.title && <h2>{eff.title}</h2>}
        {eff.text && <p>{eff.text}</p>}
        {bulletsArr.length > 0 && (
          <ul className="modal-bullets">
            {bulletsArr.map((b, i) => <li key={i}>{b}</li>)}
          </ul>
        )}
        {eff.note && <p className="modal-note"><em>{eff.note}</em></p>}
        <div className="modal-actions">
          {eff.ctaLabel && eff.ctaLink && (
            eff.ctaLink.startsWith("http") ? (
              <a href={eff.ctaLink} className="btn btn-gold" onClick={close}
                 target="_blank" rel="noopener noreferrer" data-testid="news-modal-cta">
                {eff.ctaLabel}
              </a>
            ) : (
              <Link to={eff.ctaLink} className="btn btn-gold" onClick={close} data-testid="news-modal-cta">
                {eff.ctaLabel}
              </Link>
            )
          )}
          <button className="btn btn-outline" onClick={close} data-testid="news-modal-dismiss">
            Später
          </button>
        </div>
        <label className="dont-show">
          <input
            type="checkbox"
            checked={dontShow}
            onChange={(e) => setDontShow(e.target.checked)}
            data-testid="news-dont-show-checkbox"
          />
          Nicht mehr anzeigen
        </label>
      </div>
    </div>
  );
}
